<script>
import OrganizationChart from "vue-organization-chart";
import "vue-organization-chart/dist/orgchart.css";
import WorkerImage from "@/views/Worker/Components/WorkerImage.vue";
import {onMounted, ref} from "vue";
import axios from "axios";
import {useOrganizationalChartStore} from "@/stores/OrganizationalChart";
import {storeToRefs} from "pinia";
import {useSecurityStore} from "@/stores/Security";
import useSharedData from "@/use/SharedData";

export default {
    components: {
        WorkerImage,
        OrganizationChart,
    },
    setup() {
        const {loading, backgroundColorHex} = useSharedData();

        const organizationalChartStore = useOrganizationalChartStore();
        const {organizationalChart} = storeToRefs(organizationalChartStore)
        const securityStore = useSecurityStore();
        const activeModification = ref(false);

        function findAll() {
            loading.value = true;
            axios.get("/organizationalCharts/header").then((response) => {
                    organizationalChart.value = response.data[0];
                    updateStructurePhaseOne(organizationalChart.value);
                })
                .finally(() => {
                    loading.value = false;
                });
        }

        function updateStructurePhaseOne(node) {
            // Skip the node with id 1
            if (node.id === 1) {
                // Recursively update the children of node with id 1
                if (node.children.length > 0)
                    node.children.forEach(child => updateStructurePhaseOne(child));

                return; // Exit the function for node with id 1
            }

            // For other nodes, perform the actions
            // Copy the children to savedChildren
            node.savedChildren = [...node.children]

            // Set collapsed to true only if children is not empty
            if (node.children.length > 0)
                node.collapsed = true;


            // Recursively update the children
            if (node.children.length > 0)
                node.children.forEach(child => updateStructurePhaseOne(child));


            // Empty the children array after processing
            node.children = [];
        }

        function collapse(item) {
            item.savedChildren = [...item.children];
            item.children = [];
            item.collapsed = true;
        }

        function expand(item) {
            item.children = [...item.savedChildren];
            item.savedChildren = [];
            item.collapsed = false;
        }

        onMounted(() => {
            if (securityStore.authorities['READ_ORGANIZATIONAL_CHART'])
                findAll();
        });

        return {
            securityStore,
            loading,
            organizationalChart,
            activeModification,
            backgroundColorHex,
            findAll,
            collapse,
            expand
        }

    }

};
</script>

<template>
    <div>
        <w-app :title="$t('module.organizational_chart')">
            <template v-slot:rightToolbar>
                <w-btn-reload
                    v-if="securityStore.authorities['READ_ORGANIZATIONAL_CHART']"
                    :loading="loading"
                    @press="findAll"
                />
                <w-btn-update
                    :show="!activeModification && (securityStore.authorities['SAVE_ORGANIZATIONAL_CHART'] || securityStore.authorities['UPDATE_ORGANIZATIONAL_CHART'] || securityStore.authorities['DELETE_ORGANIZATIONAL_CHART'])"
                    @press="activeModification = true"
                />
                <w-btn-close v-if="activeModification" @press="activeModification = false"/>
            </template>
            <template v-slot:content>
                <organization-chart :datasource="organizationalChart">
                    <template v-slot="{ nodeData }">
                        <v-hover v-slot="{  }">
                            <v-card elevation="24">
                                <v-card-text :style="`background-color: #009688`">

                                </v-card-text>
                                <v-row align="center" dense>
                                    <v-col cols="12">
                                        <WorkerImage
                                            :worker-id="nodeData.workerId"
                                            disable-card
                                            readonly
                                        />
                                    </v-col>
                                </v-row>
                                <v-row dense>
                                    <v-col cols="12">
                                        <strong> {{ nodeData.name }} <span v-if="nodeData.vacancy" class="red--text">(Poste Vacant)</span></strong>
                                    </v-col>
                                    <v-col cols="12">
                                        {{ nodeData.workerName }}
                                    </v-col>
                                </v-row>
                                <v-row dense>
                                    <v-col cols="12">
                                        <v-btn
                                            v-if="nodeData.collapsed && nodeData.savedChildren.length > 0"
                                            icon
                                            color="blue"
                                            @click="expand(nodeData)"
                                        >
                                            <v-icon>mdi-chevron-down-circle-outline</v-icon>
                                        </v-btn>
                                        <v-btn
                                            v-if="!nodeData.collapsed && nodeData.children.length > 0"
                                            icon
                                            color="blue"
                                            @click="collapse(nodeData)"
                                        >
                                            <v-icon>mdi-chevron-up-circle-outline</v-icon>
                                        </v-btn>
                                    </v-col>
                                </v-row>
                                <v-expand-transition>
                                    <div v-if="activeModification">
                                        <v-divider/>
                                        <v-container fluid>
                                            <v-row dense>
                                                <v-col cols="4" v-if="securityStore.authorities['DELETE_ORGANIZATIONAL_CHART'] && !nodeData.head">
                                                    <v-btn
                                                        icon
                                                        color="red"
                                                        @click="$router.push({name: 'organizational_charts.delete', params: { id: nodeData.id }})"
                                                    >
                                                        <v-icon>mdi-delete</v-icon>
                                                    </v-btn>
                                                </v-col>
                                                <v-col cols="4" v-if="securityStore.authorities['UPDATE_ORGANIZATIONAL_CHART']">
                                                    <v-btn
                                                        icon
                                                        color="orange"
                                                        @click="$router.push({ name: 'organizational_charts.edit',params: {id: nodeData.id }})"
                                                    >
                                                        <v-icon>mdi-pencil-circle-outline</v-icon>
                                                    </v-btn>
                                                </v-col>
                                                <v-col cols="4" v-if="securityStore.authorities['SAVE_ORGANIZATIONAL_CHART']">
                                                    <v-btn
                                                        icon
                                                        color="teal"
                                                        @click="$router.push({name: 'organizational_charts.add',params: { parentId: nodeData.id }})"
                                                    >
                                                        <v-icon>mdi-plus-circle-outline</v-icon>
                                                    </v-btn>
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </div>
                                </v-expand-transition>
                            </v-card>
                        </v-hover>
                    </template>
                </organization-chart>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>

<style scoped>
.orgchart-container {
    height: 100%;
    background-color: #c5c5c51a;
}


</style>